import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useLocation } from "react-router-dom";

const statusMessages: Record<string, { title: string; description: string }> = {
  proposed: { title: "📅 Nouveau rendez-vous", description: "Vous avez reçu une proposition de rendez-vous." },
  counterProposed: { title: "🔁 Contre-proposition", description: "Un nouveau créneau a été proposé pour votre rendez-vous." },
  accepted: { title: "✅ Rendez-vous confirmé", description: "Votre rendez-vous a été accepté !" },
  declined: { title: "❌ Rendez-vous refusé", description: "Votre proposition de rendez-vous a été refusée." },
  canceled: { title: "🚫 Rendez-vous annulé", description: "Un rendez-vous a été annulé." },
};

export const useAppointmentNotifications = () => {
  const { user } = useAuth();
  const location = useLocation();
  const locationRef = useRef(location.pathname);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("appointment-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "appointments" },
        (payload) => {
          const apt = payload.new as any;
          // Only notify the OTHER party
          if (apt.client_user_id !== user.id && apt.artisan_user_id !== user.id) return;
          // Don't notify the creator
          const isCreatorClient = apt.created_by === "client" && apt.client_user_id === user.id;
          const isCreatorArtisan = apt.created_by === "artisan" && apt.artisan_user_id === user.id;
          if (isCreatorClient || isCreatorArtisan) return;

          const msg = statusMessages.proposed;
          toast({ title: msg.title, description: msg.description });
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointments" },
        (payload) => {
          const apt = payload.new as any;
          const old = payload.old as any;
          if (apt.client_user_id !== user.id && apt.artisan_user_id !== user.id) return;
          if (apt.status === old.status) return; // No status change

          const msg = statusMessages[apt.status];
          if (!msg) return;

          // Don't notify the person who made the change (heuristic: skip if on messagerie)
          if (locationRef.current === "/messagerie") return;

          toast({ title: msg.title, description: msg.description });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);
};
